import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useToggleLocalMute,
  useAudioInputs,
  useVideoInputs,
  useMeetingManager,
} from 'amazon-chime-sdk-component-library-react';

import {
  pageStyle,
  waitingRoomCardStyle,
  waitingRoomHeaderStyle,
  waitingRoomTitleStyle,
  waitingRoomSubtitleStyle,
  waitingRoomContentResponsiveStyle,
  waitingRoomLeftResponsiveStyle,
  waitingRoomRightResponsiveStyle,
  waitingRoomPreviewStyle,
  waitingRoomPreviewSurfaceStyle,
  waitingRoomCameraOffOverlayStyle,
  waitingRoomCameraOffAvatarStyle,
  waitingRoomCameraOffTextStyle,
  previewControlsStyle,
  waitingRoomPreviewHeaderStyle,
  waitingRoomPreviewChipStyle,
} from './styles';
import { StyledDeviceSetupLayout } from '../../styles/Styled';
import CameraPreview from '../CameraPreview';
import WaitingRoomControls from '../WaitingRoomControls';
import WaitingRoomJoinPanel from '../WaitingRoomJoinPanel';
import useWaitingRoomAudioInputSync from '../../hooks/useWaitingRoomAudioInputSync';
import useWaitingRoomAudioOutputSync from '../../hooks/useWaitingRoomAudioOutputSync';
import useOutsideClickClose from '../../hooks/useOutsideClickClose';
import useResolvedDeviceId from '../../hooks/useResolvedDeviceId';
import playSpeakerTestTone from '../../utils/playSpeakerTestTone';

const WaitingRoom = ({
  users,
  cameraOn,
  onCameraToggle,
  onAudioInputSelected,
  onAudioOutputSelected,
  initialAudioOutputDeviceId,
  onVideoInputSelected,
  onJoin,
  onLeave,
}) => {
  const { t } = useTranslation();
  const meetingManager = useMeetingManager();
  const [showMicMenu, setShowMicMenu] = useState(false);
  const [showCameraMenu, setShowCameraMenu] = useState(false);
  const { devices: audioInputDevices, selectedDevice: selectedAudioInput } = useAudioInputs();
  const { devices: videoInputDevices, selectedDevice: selectedVideoInput } = useVideoInputs();
  const selectedMicDeviceId = useResolvedDeviceId(selectedAudioInput);
  const selectedCameraDeviceId = useResolvedDeviceId(selectedVideoInput);
  const [activeMicDeviceId, setActiveMicDeviceId] = useState(selectedMicDeviceId || '');
  const [activeSpeakerDeviceId, setActiveSpeakerDeviceId] = useState(initialAudioOutputDeviceId || '');
  const [activeCameraDeviceId, setActiveCameraDeviceId] = useState(selectedCameraDeviceId || '');
  const [micMenuDevices, setMicMenuDevices] = useState([]);
  const [speakerMenuDevices, setSpeakerMenuDevices] = useState([]);
  const { muted, toggleMute } = useToggleLocalMute();

  useEffect(() => {
    if (typeof onAudioInputSelected !== 'function') {
      return;
    }

    const deviceId = typeof selectedAudioInput === 'string'
      ? selectedAudioInput
      : selectedAudioInput?.deviceId || null;
    onAudioInputSelected(deviceId);
  }, [onAudioInputSelected, selectedAudioInput]);

  useEffect(() => {
    if (!selectedMicDeviceId) {
      return;
    }

    setActiveMicDeviceId(selectedMicDeviceId);
  }, [selectedMicDeviceId]);

  useEffect(() => {
    if (!selectedCameraDeviceId) {
      return;
    }

    setActiveCameraDeviceId(selectedCameraDeviceId);
  }, [selectedCameraDeviceId]);

  useWaitingRoomAudioInputSync({
    meetingManager,
    audioInputDevices,
    activeMicDeviceId,
    setActiveMicDeviceId,
    onAudioInputSelected,
    setMenuDevices: setMicMenuDevices,
  });

  useEffect(() => {
    if (activeCameraDeviceId || !(videoInputDevices || []).length) {
      return;
    }

    const fallbackCameraId = videoInputDevices[0]?.deviceId;
    if (!fallbackCameraId) {
      return;
    }

    setActiveCameraDeviceId(fallbackCameraId);
  }, [activeCameraDeviceId, videoInputDevices]);

  useEffect(() => {
    if (!activeCameraDeviceId || typeof onVideoInputSelected !== 'function') {
      return;
    }

    onVideoInputSelected(activeCameraDeviceId);
  }, [activeCameraDeviceId, onVideoInputSelected]);

  useOutsideClickClose({
    isOpen: showMicMenu || showCameraMenu,
    onClose: () => {
      setShowMicMenu(false);
      setShowCameraMenu(false);
    },
    shouldIgnoreTarget: (target) => (
      !!target?.closest?.('[data-wr-mic-wrap="true"]')
      || !!target?.closest?.('[data-wr-cam-wrap="true"]')
    ),
  });

  const selfUser = (users || []).find((user) => user?.self) || { fullName: t('VideoChatV2.you') };
  const displayName = selfUser.fullName || selfUser.name || t('VideoChatV2.you');
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'Y';
  const participantCount = (users || []).length || 1;

  const chooseMicDevice = useCallback(async (deviceId) => {
    try {
      await meetingManager.startAudioInputDevice(deviceId);
      setActiveMicDeviceId(deviceId);
      if (typeof onAudioInputSelected === 'function') {
        onAudioInputSelected(deviceId);
      }
    } catch (_error) {
      // Non-fatal.
    }
  }, [meetingManager, onAudioInputSelected]);

  const chooseCameraDevice = useCallback(async (deviceId) => {
    try {
      if (typeof meetingManager.startVideoInputDevice === 'function') {
        await meetingManager.startVideoInputDevice(deviceId);
      }
      setActiveCameraDeviceId(deviceId);
      if (typeof onVideoInputSelected === 'function') {
        onVideoInputSelected(deviceId);
      }
    } catch (_error) {
      // Non-fatal.
    }
    setShowCameraMenu(false);
  }, [meetingManager, onVideoInputSelected]);

  const { applySpeakerDevice } = useWaitingRoomAudioOutputSync({
    meetingManager,
    activeSpeakerDeviceId,
    setActiveSpeakerDeviceId,
    onAudioOutputSelected,
    setSpeakerMenuDevices,
    initialAudioOutputDeviceId,
  });

  const chooseSpeakerDevice = useCallback(async (deviceId) => {
    try {
      await applySpeakerDevice(deviceId);
    } catch (_error) {
      // Non-fatal.
    }
  }, [applySpeakerDevice]);

  const testSpeaker = useCallback(async () => {
    await playSpeakerTestTone(activeSpeakerDeviceId || initialAudioOutputDeviceId || '');
  }, [activeSpeakerDeviceId, initialAudioOutputDeviceId]);

  return (
    <div style={pageStyle}>
      <StyledDeviceSetupLayout>
        <div style={waitingRoomCardStyle}>
          <div style={waitingRoomHeaderStyle}>
            <h2 style={waitingRoomTitleStyle}>{t('VideoChatV2.waiting.readyToJoin')}</h2>
            <div style={waitingRoomSubtitleStyle}>
              {t('VideoChatV2.waiting.checkDevices')}
            </div>
          </div>

          <div style={waitingRoomContentResponsiveStyle}>
            <div style={waitingRoomLeftResponsiveStyle}>
              <div style={waitingRoomPreviewStyle}>
                <div style={waitingRoomPreviewSurfaceStyle}>
                  {cameraOn ? (
                    <CameraPreview />
                  ) : (
                    <div style={waitingRoomCameraOffOverlayStyle}>
                      <div style={waitingRoomCameraOffAvatarStyle}>
                        {initials}
                      </div>
                      <div style={waitingRoomCameraOffTextStyle}>{t('VideoChatV2.camera.off')}</div>
                    </div>
                  )}
                  <div style={waitingRoomPreviewHeaderStyle}>
                    <div style={waitingRoomPreviewChipStyle}>
                      {t('VideoChatV2.waiting.preview')}
                    </div>
                    <div style={waitingRoomPreviewChipStyle}>
                      {displayName}
                    </div>
                  </div>
                </div>
                <div style={previewControlsStyle}>
                  <WaitingRoomControls
                    t={t}
                    muted={muted}
                    onToggleMute={toggleMute}
                    cameraOn={cameraOn}
                    onCameraToggle={() => onCameraToggle(!cameraOn)}
                    showMicMenu={showMicMenu}
                    setShowMicMenu={setShowMicMenu}
                    showCameraMenu={showCameraMenu}
                    setShowCameraMenu={setShowCameraMenu}
                    micDevices={micMenuDevices}
                    activeMicDeviceId={activeMicDeviceId}
                    onSelectMic={chooseMicDevice}
                    speakerDevices={speakerMenuDevices}
                    activeSpeakerDeviceId={activeSpeakerDeviceId}
                    onSelectSpeaker={chooseSpeakerDevice}
                    onSpeakerTest={testSpeaker}
                    includeMicTester
                    cameraDevices={videoInputDevices}
                    activeCameraDeviceId={activeCameraDeviceId}
                    onSelectCamera={chooseCameraDevice}
                  />
                </div>
              </div>
            </div>

            <div style={waitingRoomRightResponsiveStyle}>
              <WaitingRoomJoinPanel
                t={t}
                participantCount={participantCount}
                onJoin={onJoin}
                onLeave={onLeave}
              />
            </div>
          </div>
        </div>
      </StyledDeviceSetupLayout>
    </div>
  );
};

export default WaitingRoom;
